import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KEY_MOMENTS } from "@/pages/Landing";
import { OfferDialog } from "@/components/landlord/OfferDialog";
import { TenantCard } from "@/components/landlord/TenantCard";
import { BrowseTenantsTab } from "@/components/landlord/BrowseTenantsTab";
import { PropertiesTab } from "@/components/landlord/PropertiesTab";
import { OffersTab } from "@/components/landlord/OffersTab";
import { ScreeningReport } from "@/components/ScreeningReport";
import { OnlineDot } from "@/components/OnlineDot";
import { DeleteAccountZone } from "@/components/DeleteAccountZone";
import { PageBackground } from "@/components/PageBackground";

export default function LandlordDashboard() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const matchCredits = user?.match_credits ?? 0;
  const hasUnlimited = user?.unlimited_until ? new Date(user.unlimited_until) > new Date() : false;
  const [sub, setSub] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [properties, setProperties] = useState([]);
  const [offers, setOffers] = useState([]);
  const [filters, setFilters] = useState({ min_budget: "", max_budget: "", area: "", origin_address: "", radius_miles: "25" });
  const [offerTenant, setOfferTenant] = useState(null);
  const [offerPrefill, setOfferPrefill] = useState(null);
  const [matchesFor, setMatchesFor] = useState(null);
  const [matches, setMatches] = useState([]);
  const [leaseOffer, setLeaseOffer] = useState(null);
  const [leaseText, setLeaseText] = useState("");
  const [reportTenant, setReportTenant] = useState(null);

  const loadTenants = useCallback(async () => {
    const params = {};
    if (filters.min_budget) params.min_budget = filters.min_budget;
    if (filters.max_budget) params.max_budget = filters.max_budget;
    if (filters.area) params.area = filters.area;
    if (filters.origin_address && filters.radius_miles) {
      params.origin_address = filters.origin_address;
      params.radius_miles = filters.radius_miles;
    }
    const { data } = await api.get("/landlord/tenants", { params });
    setTenants(data);
  }, [filters]);

  const loadAll = useCallback(async () => {
    const [sl, pr, of] = await Promise.all([api.get("/landlord/shortlist"), api.get("/properties"), api.get("/offers/sent")]);
    setShortlist(sl.data); setProperties(pr.data); setOffers(of.data);
    refresh?.();
  }, [refresh]);

  useEffect(() => { loadTenants(); loadAll(); }, [loadTenants, loadAll]);
  useEffect(() => { api.get("/subscription/me").then(({ data }) => setSub(data)).catch(() => {}); }, []);

  const cancelSub = async () => {
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success(`Subscription cancelled — access until ${new Date(data.access_until).toLocaleDateString()}`);
      setSub((s) => ({ ...(s || {}), auto_renew: false }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  const toggleShortlist = async (tid) => { await api.post(`/landlord/shortlist/${tid}`); loadTenants(); loadAll(); };
  const slIds = new Set(shortlist.map((s) => s.user_id));

  const viewMatches = async (p) => {
    setMatchesFor(p);
    const { data } = await api.get(`/properties/${p.id}/matches`);
    setMatches(data);
  };

  const offerFromProperty = (t, p) => {
    setOfferPrefill({ title: p.title, address: `${p.address}, ${p.city}`, rent: p.rent, lease_length_months: p.lease_length_months, move_in_date: p.available_date, deposit: p.rent, property_id: p.id });
    setOfferTenant(t);
    setMatchesFor(null);
  };

  const openOffer = (t) => { setOfferPrefill(null); setOfferTenant(t); };

  const deliverLease = async () => {
    await api.post(`/offers/${leaseOffer.id}/lease`, { content: leaseText, template: "custom" });
    toast.success("Lease delivered");
    setLeaseOffer(null); setLeaseText(""); loadAll();
  };

  const genericLease = (o) => `RESIDENTIAL LEASE AGREEMENT\n\nProperty: ${o.title}, ${o.address}\nMonthly Rent: $${o.rent}\nSecurity Deposit: $${o.deposit}\nLease Term: ${o.lease_length_months} months\nMove-in Date: ${o.move_in_date}\n\nThis agreement is made between the Landlord and Tenant. The Tenant agrees to pay rent on the 1st of each month. The Landlord agrees to maintain the premises in habitable condition.\n\nSignatures required from both parties.`;

  const openLease = (o) => { setLeaseOffer(o); setLeaseText(o.lease_content || genericLease(o)); };

  return (
    <div>
      <PageBackground variant={3} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8" data-testid="landlord-dashboard">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-3">
            <img src={KEY_MOMENTS[1].url} alt="happy tenants" className="w-10 h-10 rounded-full object-cover border-2 border-primary/20 hidden sm:block" />
            Landlord dashboard
          </h1>
          <div className="flex items-center gap-3">
            <span data-testid="match-credits" className="text-sm font-semibold px-3 py-1.5 rounded-sm bg-primary/10 text-primary">
              {hasUnlimited ? "Unlimited matches" : `${matchCredits} match${matchCredits === 1 ? "" : "es"} left`}
            </span>
            {hasUnlimited && sub?.active ? (
              sub.auto_renew ? (
                <Button variant="outline" data-testid="cancel-sub-btn" onClick={cancelSub}>Cancel subscription</Button>
              ) : (
                <span className="text-xs text-amber-700">Ends {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : ""}</span>
              )
            ) : (
              <Button variant="outline" data-testid="upgrade-btn" onClick={() => navigate("/pricing")}>Buy matches</Button>
            )}
          </div>
        </div>
        {hasUnlimited && sub?.active && sub.auto_renew && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm p-3 text-sm mb-6" data-testid="sub-active-banner">
            Unlimited plan active — auto-renews on {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : ""}.
          </div>
        )}
        {!hasUnlimited && matchCredits <= 0 && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-4 text-sm mb-6">
            You're out of match credits. Get <b>5 matches for $15</b>, <b>20 for $35</b>, or <b>unlimited/month for $50</b> on the <button className="underline" onClick={() => navigate("/pricing")}>pricing page</button>.
          </div>
        )}

        <Tabs defaultValue="browse">
          <TabsList className="mb-6">
            <TabsTrigger value="browse" data-testid="tab-browse">Browse tenants</TabsTrigger>
            <TabsTrigger value="properties" data-testid="tab-properties">Properties</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers">My offers ({offers.length})</TabsTrigger>
            <TabsTrigger value="shortlist" data-testid="tab-shortlist">Shortlist ({shortlist.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="browse">
            <BrowseTenantsTab
              filters={filters} setFilters={setFilters} onApply={loadTenants}
              tenants={tenants} slIds={slIds} onShortlist={toggleShortlist}
              onViewReport={setReportTenant} onOffer={openOffer}
            />
          </TabsContent>

          <TabsContent value="properties">
            <PropertiesTab properties={properties} onCreated={loadAll} onViewMatches={viewMatches} />
          </TabsContent>

          <TabsContent value="offers">
            <OffersTab offers={offers} onMessage={() => navigate("/messages")} onLease={openLease} />
          </TabsContent>

          <TabsContent value="shortlist">
            {shortlist.length === 0 ? (
              <div className="bg-white border border-border rounded-sm p-12 text-center text-muted-foreground">No shortlisted tenants.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {shortlist.map((t) => (<TenantCard key={t.user_id} t={t} shortlisted onShortlist={toggleShortlist} onViewReport={setReportTenant} onOffer={openOffer} />))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DeleteAccountZone />
      </div>

      {offerTenant && <OfferDialog tenant={offerTenant} open={!!offerTenant} prefill={offerPrefill} onClose={() => setOfferTenant(null)} onSent={loadAll} />}

      {/* Screening report dialog */}
      <Dialog open={!!reportTenant} onOpenChange={(o) => !o && setReportTenant(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Screening report — {reportTenant?.name}</DialogTitle></DialogHeader>
          {reportTenant && <ScreeningReport profile={reportTenant} compact />}
        </DialogContent>
      </Dialog>

      {/* Matches dialog */}
      <Dialog open={!!matchesFor} onOpenChange={(o) => !o && setMatchesFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Tenants matching {matchesFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {matches.length === 0 && <div className="text-sm text-muted-foreground">No matching active tenants yet.</div>}
            {matches.map((t) => (
              <div key={t.user_id} className="border border-border rounded-sm p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold flex items-center gap-2">{t.name} <OnlineDot online={t.online} /> {t.interested && <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Interested</span>}</div>
                  <div className="text-xs text-muted-foreground">${t.budget_min}–${t.budget_max} · {(t.preferred_areas || []).join(", ")} · credit {t.credit_score}</div>
                </div>
                <Button size="sm" data-testid={`match-offer-${t.user_id}`} onClick={() => offerFromProperty(t, matchesFor)}>Send offer</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lease dialog */}
      <Dialog open={!!leaseOffer} onOpenChange={(o) => !o && setLeaseOffer(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Deliver lease — {leaseOffer?.title}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Edit the generic template or paste your own lease text.</p>
          <Textarea data-testid="lease-text" value={leaseText} onChange={(e) => setLeaseText(e.target.value)} rows={10} className="text-sm" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaseOffer(null)}>Cancel</Button>
            <Button data-testid="lease-deliver" onClick={deliverLease}>Deliver to tenant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
